import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Mail, Lock, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const TechLogin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/auth/tech-login`, formData);
      // CRITICAL: Clear any stale owner session FIRST to prevent session contamination
      localStorage.removeItem("token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("user");
      // Set technician session
      localStorage.setItem("tech_token", res.data.token);
      if (res.data.refresh_token) {
        localStorage.setItem("tech_refresh_token", res.data.refresh_token);
      }
      localStorage.setItem("tech_user", JSON.stringify({
        member_id: res.data.member_id, name: res.data.name,
        email: res.data.email, role: "technician",
        business_user_id: res.data.owner_id || ""
      }));
      localStorage.setItem("auth_kind", "tech"); // marker for silent-refresh interceptor
      toast.success(`Welcome back, ${res.data.name}!`);
      navigate("/tech/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden" style={{background: '#06060b'}}>
      {/* Background Decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full opacity-[0.03]" style={{background: '#FFCC00', filter: 'blur(120px)'}} />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full opacity-[0.02]" style={{background: '#0046BE', filter: 'blur(100px)'}} />
      </div>

      <div className="w-full max-w-sm relative z-10">
        {/* Logo & Title */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-2xl mx-auto mb-5 flex items-center justify-center" style={{background: 'linear-gradient(135deg, #FFCC00 0%, #E6B800 100%)', boxShadow: '0 0 40px rgba(255,204,0,0.15)'}}>
            <span className="text-2xl font-black text-black font-['Manrope']">F</span>
          </div>
          <h1 className="text-2xl font-bold text-white font-['Manrope']">Technician Portal</h1>
          <p className="text-white/30 mt-2 text-sm">Sign in to view your jobs and earnings</p>
        </div>

        {/* Form */}
        <div className="rounded-2xl p-6 sm:p-7" style={{background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)'}}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-white/50 text-xs font-medium uppercase tracking-wider">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" />
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="h-11 pl-10 rounded-xl bg-white/[0.03] border-white/[0.08] text-white placeholder:text-white/20 focus:border-[#FFCC00]/30 focus:ring-[#FFCC00]/10"
                  placeholder="your@email.com"
                  required
                  data-testid="tech-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-white/50 text-xs font-medium uppercase tracking-wider">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" />
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="h-11 pl-10 rounded-xl bg-white/[0.03] border-white/[0.08] text-white placeholder:text-white/20 focus:border-[#FFCC00]/30 focus:ring-[#FFCC00]/10"
                  placeholder="Enter your password"
                  required
                  data-testid="tech-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-11 mt-2 rounded-xl font-semibold text-sm transition-all duration-200"
              style={{background: 'linear-gradient(135deg, #FFCC00 0%, #E6B800 100%)', color: '#000', boxShadow: '0 0 20px rgba(255,204,0,0.15)'}}
              disabled={loading}
              data-testid="tech-login-btn"
            >
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Signing in...</> : "Sign In"}
            </Button>
          </form>
        </div>

        <div className="mt-6 text-center">
          <Link to="/login" className="text-white/30 hover:text-white/50 text-sm inline-flex items-center gap-1.5 transition-colors" data-testid="back-to-main-link">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to main login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TechLogin;
